import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004';

function PurchaseSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { downloadInfo: initialDownloadInfo, modName, purchaseId } = location.state || {};
  const [resolvedDownloadInfo, setResolvedDownloadInfo] = useState(initialDownloadInfo?.type === 'gdrive' ? initialDownloadInfo : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only trigger if we have a code and haven't resolved yet
    if (initialDownloadInfo?.code && initialDownloadInfo?.type === 'code' && !resolvedDownloadInfo) {
      resolveDownloadLink(initialDownloadInfo.code);
    }
  }, [initialDownloadInfo]); // Only depend on initialDownloadInfo

  const resolveDownloadLink = async (code) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      // Check if the response is actually JSON before parsing
      const contentType = response.headers.get("content-type");
      if (response.ok && contentType && contentType.includes("application/json")) {
        const data = await response.json();
        if (data.downloadInfo) {
          // Add a console log here to see exactly what the backend sends
          console.log("Resolved Data:", data.downloadInfo);
          setResolvedDownloadInfo(data.downloadInfo);
        } else {
          setResolvedDownloadInfo({ type: 'error', message: 'Invalid response from server.' });
        }
      } else {
        console.error("Server returned an error or non-JSON response:", response.status, response.statusText);
        setResolvedDownloadInfo({ type: 'error', message: 'Server error occurred.' });
      }
    } catch (error) {
      console.error('Network Error:', error);
      setResolvedDownloadInfo({ type: 'error', message: 'Network error occurred.' });
    } finally {
      setLoading(false);
    }
  };

  if (!initialDownloadInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="text-gray-400">No purchase information found</div>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Back to Marketplace
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4">
      <div className="max-w-2xl w-full bg-surface-dark border border-white/5 rounded-2xl p-8 shadow-2xl">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
            <span className="material-symbols-outlined text-green-500 text-5xl">check_circle</span>
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-white text-center mb-2">Purchase Successful!</h1>
        <p className="text-gray-400 text-center mb-8">
          Thank you for your purchase of <span className="text-white font-medium">{modName}</span>
        </p>

        {/* Download Information */}
        <div className="bg-background-dark border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Download Information</h2>

          {loading ? (
            <div className="text-center py-4 text-gray-400 italic">
              Fetching download details...
            </div>
          ) : resolvedDownloadInfo?.type === 'gdrive' ? (
            /* Show the Drive Link */
            <div className="space-y-4">
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 text-sm mb-2 font-medium">Link Resolved Successfully!</p>
                <a
                  href={resolvedDownloadInfo.link}
                  target="_blank"
                  className="text-primary underline break-all text-sm"
                >
                  {resolvedDownloadInfo.link}
                </a>
              </div>
              <button
                onClick={() => window.open(resolvedDownloadInfo.link, '_blank')}
                className="w-full py-3 bg-primary text-white font-bold rounded-lg hover:bg-blue-600 transition-colors"
              >
                Download File
              </button>
            </div>
          ) : (
            /* Show the Code or the Error Message */
            <div>
              <div className="flex items-center justify-between bg-surface-dark border border-white/10 p-4 rounded-lg">
                <code className="text-xl font-mono text-primary font-bold">{initialDownloadInfo.code}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(initialDownloadInfo.code)}
                  className="text-xs bg-white/10 px-2 py-1 rounded"
                >
                  Copy
                </button>
              </div>
              {resolvedDownloadInfo?.message && (
                <p className="mt-4 text-orange-400 text-sm bg-orange-400/10 p-3 rounded border border-orange-400/20">
                  <span className="material-symbols-outlined align-middle mr-2 text-sm">info</span>
                  {resolvedDownloadInfo.message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-blue-400 mt-0.5">info</span>
            <div className="text-sm text-gray-300">
              <p className="font-medium text-white mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-400">
                <li>A confirmation email has been sent to your email address</li>
                <li>Keep your download code/link safe for future reference</li>
                <li>For support, contact us with your purchase ID: <code className="text-primary">{purchaseId}</code></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors border border-white/10"
          >
            Back to Marketplace
          </button>
          <button
            onClick={() => window.print()}
            className="flex-1 px-6 py-3 bg-primary hover:bg-blue-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined">print</span>
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

export default PurchaseSuccessPage;
